import React from "react";
import { renderPreview } from "../../../../SectionElements/plugins/utils";
import RenderHtml from "../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import CanvasNode from "../../../../Documents/components/Nodes/CanvasNode";

/**
 * MoveChildNode Component
 * Renders a child node (attachment, video, rich-text, form, canva, canvas) for move position selection
 */
const MoveChildNode = ({
  child,
  parentContext,
  globalNodeNumber,
  sectionId,
  stepId,
  substepId,
  preloadedData,
  checkboxState,
  moveNodeType,
  checkboxLabelColor,
  customOnNavigate,
  shouldDisableChildCheckbox,
  shouldShowChildCheckbox,
}) => {
  const childType = child.type;
  const normalizedType = childType === 'canvas' ? 'canva' : childType;
  
  // Get node heading based on type
  const getNodeHeading = (type, number) => {
    const typeNames = {
      'rich-text': 'Rich Text',
      'video': 'Video',
      'form': 'Form',
      'canva': 'Canvas',
      'canvas': 'Canvas',
      'attachment': 'Attachment'
    };
    return `${typeNames[type] || type} ${number}`;
  };

  const nodeHeading = getNodeHeading(childType, globalNodeNumber);

  // Get step and substep IDs from parent context
  const stepIdRef = stepId ? (preloadedData
    .flatMap(s => s.steps || [])
    .find(s => s.tree_node_id === stepId)?.step_id_ref) : null;
  const substepIdRef = substepId ? (preloadedData
    .flatMap(s => s.steps || [])
    .flatMap(st => st.substeps || [])
    .find(s => s.tree_node_id === substepId)?.substep_id_ref) : null;

  // Determine checkbox state
  // For canvas elements, check both detail.canvas_tree_node_id and tree_node_id
  const canvasNodeId = child.detail?.canvas_tree_node_id || child.tree_node_id;
  const isCurrent = checkboxState?.currentNodeId === canvasNodeId && 
                    (checkboxState?.currentNodeType === childType || 
                     checkboxState?.currentNodeType === normalizedType);
  const isSourceChild = checkboxState?.sourceNodeId === canvasNodeId || 
                       checkboxState?.sourceNodeId === child.tree_node_id ||
                       checkboxState?.sourceNodeId === child.detail?.canvas_tree_node_id;
  
  // Show checkbox based on move type and source node
  const shouldDisable = shouldDisableChildCheckbox(child, stepId, stepIdRef, substepId, substepIdRef, sectionId);
  const showCheckbox = shouldShowChildCheckbox(child, stepId, stepIdRef, substepId, substepIdRef, sectionId) || shouldDisable;
  const isDisabled = shouldDisable || (checkboxState?.allChildrenDisabled && !isCurrent) || isSourceChild;
  
  // Source node's children should always be checked
  const isChecked = isCurrent || checkboxState?.allChildrenChecked || isSourceChild || shouldDisable;

  return (
    <div key={canvasNodeId} style={{ marginTop: "20px", clear: "both" }}>
      {/* Always show heading, conditionally show checkbox */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "6px",
            alignItems: "center",
          }}
        >
          {showCheckbox && (
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled && !isSourceChild}
              onChange={() => {
                if (isSourceChild) return;
                if (!isDisabled && customOnNavigate) {
                  customOnNavigate(childType, child, {
                    ...parentContext,
                    nodeNumber: globalNodeNumber,
                    nodeIndex: parentContext.nodeIndex || 0
                  });
                }
              }}
              style={{ 
                cursor: isDisabled ? "not-allowed" : (isSourceChild ? "not-allowed" : "pointer"),
                accentColor: isSourceChild ? "black" : undefined,
              }}
            />
          )}
          <h2 
            onClick={() => {
              if (isSourceChild) return;
              if (!isDisabled && showCheckbox && customOnNavigate) {
                customOnNavigate(childType, child, {
                  ...parentContext,
                  nodeNumber: globalNodeNumber,
                  nodeIndex: parentContext.nodeIndex || 0
                });
              }
            }}
            style={{ 
              fontSize: "20px", 
              margin: 0,
              cursor: isSourceChild ? "not-allowed" : ((!isDisabled && showCheckbox) ? "pointer" : "default"),
              color: !isDisabled && showCheckbox ? (checkboxLabelColor || undefined) : undefined,
              userSelect: "none"
            }}
          >{nodeHeading}</h2>
        </div>

        <div
          style={{
            color: "#888",
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          Section {parentContext.sectionIndex + 1} &gt; Step{" "}
          {parentContext.stepIndex}
          {parentContext.substepLetter && (
            <> &gt; Substep {parentContext.stepIndex}{parentContext.substepLetter}</>
          )}
        </div>
      </div>

      {/* Render child content based on type */}
      {childType === 'rich-text' && (
        <>
          {/* <div style={{ marginLeft: "5px", whiteSpace: 'pre-wrap' }}>
            {child.detail?.short_description}
          </div> */}
          <RenderHtml content={child.detail?.short_description || ""} />
          <div style={{ marginLeft: "4px", marginTop: "5px" }}>
            {child.detail?.category_info?.category_name}
          </div>
          <div
            style={{
              fontSize: "14px",
              marginLeft: "4px",
              marginTop: "5px",
            }}
          >
            <div
              style={{ marginTop: "-6px" }}
              dangerouslySetInnerHTML={{
                __html: child.detail?.rich_text_content || "",
              }}
            />
          </div>
        </>
      )}

      {childType === 'video' && (
        <>
          {child.detail?.video && (
            <video
              controls
              style={{ width: "100%", marginTop: "10px", height: "400px" }}
              src={child.detail.video}
            >
              Your browser does not support the video tag.
            </video>
          )}
          {(child.detail?.video_url || 
            (child.detail?.videos && child.detail.videos[0]?.video_url)) && (
            <div style={{ marginTop: "10px" }}>
              {child.detail?.video_url || 
               (child.detail?.videos && child.detail.videos[0]?.video_url)}
            </div>
          )}
          <RenderHtml content={child.detail?.short_description || ""} />
        </>
      )}

      {childType === 'attachment' && (
        <>
          <RenderHtml content={child.detail?.short_description || ""} />
          {(child.detail?.attachments || []).map((attachment, i) => (
            <div key={i} style={{ marginLeft: "20px" }}>
              {renderPreview(attachment)}
            </div>
          ))}
        </>
      )}

      {(childType === 'canva' || childType === 'canvas') && (
        <>
        <RenderHtml content={child.detail?.short_description || ""} />
        <CanvasNode
          node={child}
          nodeNumber={globalNodeNumber}
          nodeType={childType}
          onNavigate={customOnNavigate}
          checkboxState={checkboxState}
          disabled={isDisabled}
          showCheckbox={false}
          showHeading={false}
          checkboxLabelColor={checkboxLabelColor}
        />
      </>
      )}

      {childType === 'form' && (
        <RenderHtml content={child.detail?.short_description || ""} />
      )}
    </div>
  );
};

export default MoveChildNode;